import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation} from '@angular/core';
import {VerticalMenuItem} from '../../models/vertical-menu.model';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {filter} from 'rxjs/operators';
import {NavigationEnd, Router} from '@angular/router';
import {GenericFunctionsUtil} from '../../utils/generic-functions.util';
import {OverlayPanelComponent} from '../overlay-panel/overlay-panel.component';
import {Subscription} from 'rxjs';

@Component({
  selector: 'si-vertical-menu',
  templateUrl: './vertical-menu.component.html',
  styleUrls: ['./vertical-menu.component.scss'],
  animations: [
    trigger('submenu', [
      state('hidden', style({
        height: '0',
        overflow: 'hidden'
      })),
      state('visible', style({
        height: '*'
      })),
      transition('visible <=> hidden', [style({overflow: 'hidden'}), animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')]),
      transition('void => *', animate(0))
    ])
  ],
  encapsulation: ViewEncapsulation.None
})
export class VerticalMenuComponent implements OnInit, OnDestroy {
  @Input() styleClass: string;

  // Hides search input.
  @Input() hideSearch = false;

  // Search on first level of items or ignore it.
  @Input() searchOnFirstLevel = true;

  // If true, the menu can have more than one item expanded at the same time.
  @Input() expandMultiple = true;

  @Output() itemClicked = new EventEmitter<VerticalMenuItem>();
  @ViewChild(OverlayPanelComponent) overlayPanel: OverlayPanelComponent;
  subscriptions: Subscription[] = [];
  filteredItems: VerticalMenuItem[];
  searchModel: string;
  highlightSearchModel: string;
  showSearchContainer = false;
  searchLinkClicked = false;

  // Array of items without recursive parents.
  originalItems: VerticalMenuItem[];

  private _items: VerticalMenuItem[];

  constructor(private router: Router) {
  }

  get items(): VerticalMenuItem[] {
    return this._items;
  }

  @Input()
  set items(val: VerticalMenuItem[]) {
    this._items = val;
    this.originalItems = GenericFunctionsUtil.deepClone(this._items);
    this.setParents(this._items);
    this.setActiveStates(this._items, true);
  }

  ngOnInit(): void {
    this.subscriptions.push(this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.onNavigationEnd())
    );
  }

  onNavigationEnd(): void {
    this.setActiveStates(this.items, this.searchLinkClicked);
    this.searchLinkClicked = false;
  }

  onFocusSearchInput(): void {
    this.showSearchContainer = true;
  }

  onChangeSearchModel(): void {
    this.showSearchContainer = true;
    if (!this.searchModel) {
      this.filteredItems = null;
      return;
    }

    this.filteredItems = this.getFilteredItems();
    this.highlightSearchModel = this.searchModel;
  }

  getFilteredItems(): VerticalMenuItem[] {
    const items = GenericFunctionsUtil.deepClone(this.originalItems);
    this.setParents(items);
    return items.filter(findMatchItem, this);

    function findMatchItem(item: VerticalMenuItem): boolean {
      const normalizedLabel = GenericFunctionsUtil.normalizeString(item.label?.toLowerCase());
      const normalizedSearchLabel = GenericFunctionsUtil.normalizeString(this.searchModel?.toLowerCase());
      if (normalizedLabel.includes(normalizedSearchLabel) && (this.searchOnFirstLevel || item.parent) && !item.hide) {
        expandItem(item);
        return true;
      }

      if (item.items?.length) {
        const found = !!(item.items = item.items.filter(findMatchItem, this)).length;
        if (found) {
          expandItem(item);
        }

        return found;
      }
    }

    function expandItem(item: VerticalMenuItem): void {
      item.expanded = true;
      if (item.items?.length) {
        item.items.map(expandItem);
      }
    }
  }

  onClickSearchLink(item: VerticalMenuItem): void {
    this.showSearchContainer = false;
    this.searchLinkClicked = true;
    this.itemClicked.emit(item);
  }

  onClickItem(event: Event, item: VerticalMenuItem): void {
    if (item.disabled) {
      event.preventDefault();
      return;
    }

    item.expanded = !item.expanded;
    if (!this.expandMultiple) {
      const toExpand = item.expanded;
      this.collapseItems(this.items);
      if (toExpand) {
        this.expandItem(item);
      } else if (item.parent) {
        this.expandItem(item.parent);
      }
    }

    if (item.command) {
      item.command({
        originalEvent: event,
        item
      });
    }

    this.itemClicked.emit(item);
  }

  /**
   * Collapse all items of the menu.
   * @param items Items to be collapsed.
   */
  collapseItems(items: VerticalMenuItem[]): void {
    items.forEach(item => {
      if (item.items?.length) {
        this.collapseItems(item.items);
      }

      item.expanded = false;
    });
  }

  /**
   * Expands an item (to the root).
   * @param item Item to be expanded.
   */
  expandItem(item: VerticalMenuItem): void {
    item.expanded = true;
    if (item.parent) {
      this.expandItem(item.parent);
    }
  }

  /**
   * Sets the parent in the item.
   * @param items Items to set the parent to.
   * @param parentItem Item to be the parent.
   */
  setParents(items: VerticalMenuItem[], parentItem?: VerticalMenuItem): void {
    items.forEach(item => {
      if (parentItem) {
        item.parent = parentItem;
      }

      if (item.items?.length) {
        this.setParents(item.items, item);
      }
    });
  }

  // Recursive function to set active and expanded attributes in each item of the tree.
  setActiveStates(items: VerticalMenuItem[], expandActiveItems = false): boolean {
    let isActive = false;
    for (const item of items) {
      let isActiveAux = false;
      if (item.items?.length) {
        isActiveAux = this.setActiveStates(item.items, expandActiveItems);
      }

      item.active = isActiveAux || this.checkActiveState(item.routerLink || item.state);
      if (expandActiveItems) {
        item.expanded = item.active;
      }

      if (item.active) {
        isActive = item.active;
      }
    }

    return isActive;
  }

  checkActiveState(givenLink: string): boolean {
    if (!givenLink) {
      return false;
    }

    if (givenLink === '/') {
      return givenLink === this.router.url;
    }

    const givenLinkParts = givenLink.split('/');
    const routerUrlParts = this.router.url.split('/');
    return givenLinkParts.every((val, index) => val === routerUrlParts[index]);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
